/* ═══════════════════════════════════════════════════════════════════════
   NoteFlow — docx-notes.ts
   Notes/disclosure HTML-to-DOCX parser. Walks the rendered notes DOM and
   converts headings, paragraphs, lists, and tables into docx Paragraph /
   Table objects.
   ═══════════════════════════════════════════════════════════════════════ */

import {
  Paragraph, Table, TableRow, TableCell, TextRun,
  HeadingLevel, WidthType,
} from 'docx';
import { BORDER_BOTTOM } from './docx-helpers';

export function parseNotesToParagraphs(): Paragraph[] {
  const notesEl = document.getElementById('notes-statement');
  if (!notesEl || !notesEl.innerHTML.trim()) {
    return [
      new Paragraph({
        spacing: { after: 200 },
        children: [
          new TextRun({
            text: 'Notes have not been generated yet. Complete the Notes questionnaire to include notes in this package.',
            italics: true,
            size: 20,
            font: 'Calibri',
          }),
        ],
      }),
    ];
  }

  const paragraphs: Paragraph[] = [];
  const html = notesEl.innerHTML;

  // Parse the HTML into a temporary container
  const container = document.createElement('div');
  container.innerHTML = html;

  function processNode(node: Node): void {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node.textContent || '').trim();
      if (text) {
        paragraphs.push(
          new Paragraph({
            spacing: { after: 120 },
            children: [new TextRun({ text, size: 20, font: 'Calibri' })],
          }),
        );
      }
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();

    // Headings — Note X titles
    if (tag === 'h2' || tag === 'h3' || tag === 'h4') {
      const text = el.textContent || '';
      paragraphs.push(
        new Paragraph({
          heading: tag === 'h2' ? HeadingLevel.HEADING_2 : HeadingLevel.HEADING_3,
          spacing: { before: 240, after: 120 },
          children: [
            new TextRun({
              text,
              bold: true,
              size: tag === 'h2' ? 24 : 22,
              font: 'Calibri',
              color: '1E293B',
            }),
          ],
        }),
      );
      return;
    }

    // Tables
    if (tag === 'table') {
      const tblRows: TableRow[] = [];
      const htmlRows = el.querySelectorAll('tr');
      htmlRows.forEach((tr) => {
        const cells: TableCell[] = [];
        tr.querySelectorAll('td, th').forEach((td) => {
          const isHeader = td.tagName.toLowerCase() === 'th';
          cells.push(
            new TableCell({
              borders: BORDER_BOTTOM,
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: td.textContent || '',
                      bold: isHeader,
                      size: 18,
                      font: 'Calibri',
                    }),
                  ],
                }),
              ],
            }),
          );
        });
        if (cells.length > 0) {
          tblRows.push(new TableRow({ children: cells }));
        }
      });
      if (tblRows.length > 0) {
        paragraphs.push(
          new Paragraph({ spacing: { before: 120 }, children: [] }),
        );
        paragraphs.push(
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows: tblRows,
          }) as unknown as Paragraph,
        );
        paragraphs.push(
          new Paragraph({ spacing: { after: 120 }, children: [] }),
        );
      }
      return;
    }

    // Paragraphs and divs
    if (tag === 'p' || tag === 'div') {
      const text = el.textContent || '';
      if (text.trim()) {
        // Check if it looks like a note heading (e.g. "Note 1 —")
        const isNoteHeading = /^Note\s+\d/.test(text.trim());
        if (isNoteHeading) {
          paragraphs.push(
            new Paragraph({
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 240, after: 120 },
              children: [
                new TextRun({
                  text,
                  bold: true,
                  size: 24,
                  font: 'Calibri',
                  color: '1E293B',
                }),
              ],
            }),
          );
        } else {
          paragraphs.push(
            new Paragraph({
              spacing: { after: 120 },
              children: [new TextRun({ text, size: 20, font: 'Calibri' })],
            }),
          );
        }
      }
      return;
    }

    // Lists
    if (tag === 'ul' || tag === 'ol') {
      el.querySelectorAll('li').forEach((li) => {
        paragraphs.push(
          new Paragraph({
            spacing: { after: 60 },
            indent: { left: 360 },
            children: [
              new TextRun({ text: '•  ', size: 20, font: 'Calibri' }),
              new TextRun({ text: li.textContent || '', size: 20, font: 'Calibri' }),
            ],
          }),
        );
      });
      return;
    }

    // Strong / bold
    if (tag === 'strong' || tag === 'b') {
      const text = el.textContent || '';
      if (text.trim()) {
        paragraphs.push(
          new Paragraph({
            spacing: { after: 120 },
            children: [new TextRun({ text, bold: true, size: 20, font: 'Calibri' })],
          }),
        );
      }
      return;
    }

    // Fallback: recurse into children
    el.childNodes.forEach((child) => processNode(child));
  }

  container.childNodes.forEach((child) => processNode(child));

  // If nothing was parsed, fall back to plain text
  if (paragraphs.length === 0) {
    const text = notesEl.innerText.trim();
    text.split('\n').forEach((line) => {
      if (!line.trim()) return;
      const isNoteHeading = /^Note\s+\d/.test(line.trim());
      paragraphs.push(
        new Paragraph({
          heading: isNoteHeading ? HeadingLevel.HEADING_2 : undefined,
          spacing: { after: isNoteHeading ? 120 : 100 },
          children: [
            new TextRun({
              text: line,
              bold: isNoteHeading,
              size: isNoteHeading ? 24 : 20,
              font: 'Calibri',
              color: isNoteHeading ? '1E293B' : undefined,
            }),
          ],
        }),
      );
    });
  }

  return paragraphs;
}
